// Copyright (c) StrangeBee 2024

import React, { ReactElement, useEffect, useState } from 'react';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { THForm } from '@Molecules/THForm/THForm';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import Upload from 'antd/lib/upload';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { FormSubmitButton, FormSubmitOptions } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { ICONS } from '@Constants/iconsApp';

import './TaskLogForm.scss';

export interface TaskLogValuesForm extends FormSubmitOptions {
    message: string;
    includeInTimeline?: number;
    attachments: File[];
}

interface TaskFormProps {
    form: FormInstance;
    defaultMessage?: string;
    defaultAttachments?: any[];
    defaultIncludeInTimeline?: number;
    isValid: boolean;
    onFinish: (values: TaskLogValuesForm) => Promise<any>;
    onChange: () => void;
    onCancel: () => void;
    isTaskClosed?: boolean;
}

export const TaskLogForm = ({
    form,
    defaultMessage = '',
    defaultAttachments,
    defaultIncludeInTimeline,
    isValid,
    onFinish,
    onChange,
    onCancel,
    isTaskClosed,
}: TaskFormProps): ReactElement => {
    const i = useLocalMessage();
    const [fileListUpload, setFileListUpload] = useState<(RcFile | undefined)[]>([]);
    const [initialAttachmentIds, setInitialAttachmentIds] = useState<string[]>([]);
    const [initialAttachment, setInitialAttachment] = useState<any[]>(defaultAttachments || []);
    const [attachmentsRemoved, setAttachmentsRemoved] = useState<string[]>([]);
    const [showIncludeInTimeline, setShowIncludeInTimeline] = useState<boolean>(!!defaultIncludeInTimeline);
    const [stateLoading, setStateLoading] = useState(false);

    const initValue = {
        message: defaultMessage,
        attachments: [],
        includeInTimeline: defaultIncludeInTimeline || Math.round(Date.now()),
    };

    const onSwitchHandler = (value: boolean) => {
        setShowIncludeInTimeline(value);
        onChange();
    };

    const onFinishHandler = (value: TaskLogValuesForm) => {
        let opt = {};

        if (attachmentsRemoved.length) {
            opt = {
                attachmentsRemoved,
            };
        }

        opt = {
            ...opt,
            includeInTimeline: showIncludeInTimeline
                ? value.includeInTimeline?.valueOf() || Math.round(Date.now())
                : null,
        };

        setStateLoading(true);
        onFinish({ ...value, attachments: fileListUpload as File[], ...opt }).finally(() => {
            setStateLoading(false);
        });
    };

    const onChangeUpload = ({ fileList }: UploadChangeParam<UploadFile<any>>) => {
        if (fileList.length) {
            const newFileList = fileList.map(({ originFileObj }) => originFileObj);
            setFileListUpload(newFileList);
        }
        if (fileList.length === 0) {
            setFileListUpload([]);
        }
        onChange();
    };

    const onRemoveHandler = (id: string) => {
        if (initialAttachmentIds.includes(id)) {
            setAttachmentsRemoved((lf) => [...lf, id]);
            setInitialAttachment((lf) => lf.filter(({ _id }) => _id !== id));
            onChange();
        }
    };

    useEffect(() => {
        const treatAttachment = defaultAttachments?.map(({ _id }) => _id);
        setInitialAttachmentIds(treatAttachment || []);
    }, [defaultAttachments]);

    return (
        <THForm<TaskLogValuesForm>
            name="task-log-form"
            onFinish={onFinishHandler}
            form={form}
            initialValues={initValue}
            onValuesChange={onChange}
            submitTouchedValues
            className="task-log-form"
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        loading={stateLoading}
                        onAnother={
                            !isTaskClosed
                                ? () => {
                                      form.setFieldsValue({
                                          another: true,
                                      });
                                      form.submit();
                                  }
                                : undefined
                        }
                        anotherText="Save and close the task"
                        onCancel={() => {
                            onCancel();
                        }}
                        disabled={!isValid}
                    />
                }
            >
                <THForm.Item
                    name="message"
                    label={i('task-log.create.logDescription')}
                    rules={[{ required: true, message: i('task-log.create.required') }]}
                >
                    <THMarkdown sameSize />
                </THForm.Item>
                <div className="task-log-form__intimeline">
                    <THForm.Item label={i('task-log.create.includeInTimeline')}>
                        <THSwitch onChange={onSwitchHandler} defaultChecked={!!defaultIncludeInTimeline} />
                    </THForm.Item>
                    <THForm.Item
                        name="includeInTimeline"
                        label={i('task-log.create.createdDate')}
                        hidden={!showIncludeInTimeline}
                        className="task-log-form__intimeline-date"
                        rules={[{ required: showIncludeInTimeline, message: i('task-log.create.required') }]}
                    >
                        <THDatePicker />
                    </THForm.Item>
                </div>

                <THForm.Item label={i('general.import.upload.label')}>
                    {initialAttachment.length ? (
                        <ul className="list-attachment">
                            {initialAttachment.map((item) => (
                                <li key={item._id} className="list-attachment__item">
                                    <span>{item.name}</span>
                                    <THButton
                                        type="link"
                                        danger
                                        icon={<ICONS.DeleteIcon />}
                                        onClick={() => onRemoveHandler(item._id)}
                                    />
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    <Upload.Dragger
                        beforeUpload={(): boolean => false}
                        multiple
                        onChange={onChangeUpload}
                        className="task-log-form__upload"
                        showUploadList={{
                            showDownloadIcon: false,
                            showRemoveIcon: true,
                            removeIcon: <ICONS.DeleteIcon />,
                        }}
                    >
                        <span className="task-log-form-file__text">{i('general.import.upload.content')}</span>
                    </Upload.Dragger>
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
